import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import {
  Select,
  NumberInput,
  Checkbox,
  Text,
  Modal,
  Group,
  Paper,
  Button,
  Stack,
} from "@mantine/core";
import { Cubby } from "../../stores/Cubby";
import {
  EACH_CLOSURE_MIGHT_COST,
  MIN_CHANNEL_SIZE_SATS,
  TEST_CHANNEL_OPENS_WITH_THIS_SATOSHIS as TEST_FEE_RATES_WITH_THIS_LOW_SATOSHIS,
  MODALS,
} from "../../helpers/constants";
import GenericEstimatedFee from "../genericEstimated";
import { TextWithInfo } from "./textWithInfo";
import SatoshisInfo from "../interactiveData/satoshisInfo";
import { TextWithInfoAndIcon } from "../dataDisplay/textWithInfoAndIcon";
import { ARTICLES } from "../../docs/docsIndex";
import { IconExchange } from "@tabler/icons-react";

type ComboboxItem = {
  value: string;
  label: string;
};

const OpenChannelToOtherNode = observer(({ cubby }: { cubby: Cubby }) => {
  const [error, setError] = useState("");
  const [satoshisError, setSatoshisError] = useState("");
  const [isPrivate, setIsPrivate] = useState(true);
  const [isLoading, setIsLoading] = useState(false); // Add isLoading state

  const recommendedPublicKeyForChannelOpen =
    cubby.recommended_lsp_node_public_key;

  const peersData = cubby.peerLists.data.map((peer) => ({
    value: peer.public_key,
    label: `${peer.alias || ""} (${peer.public_key.slice(0, 7)})`,
  }));

  const defaultPeer =
    peersData.find(
      (peer) => peer.value === recommendedPublicKeyForChannelOpen
    ) || null;

  const [selectedPeer, setSelectedPeer] = useState<ComboboxItem | null>(
    defaultPeer
  );

  useEffect(() => {
    if (cubby.hasEnoughFundsOnchainToOpenChannel) {
      cubby.getFeeEstimateForArbitrarySatoshisAmount(
        TEST_FEE_RATES_WITH_THIS_LOW_SATOSHIS
      );
    }
  }, []);

  const handlePeerChange = (_value: string, option: ComboboxItem) => {
    setSelectedPeer(option);
    cubby.getFeeEstimateForArbitrarySatoshisAmount(
      TEST_FEE_RATES_WITH_THIS_LOW_SATOSHIS
    );
  };

  const handleSatoshisChange = (newValue: number) => {
    cubby.updateSatoshisAmountForOpenChannel(newValue);
    if (newValue < cubby.amountThatCouldBeUsedToOpenChannels) {
      cubby.getFeeEstimateForArbitrarySatoshisAmount(
        TEST_FEE_RATES_WITH_THIS_LOW_SATOSHIS
      );
    }
    if (
      newValue < MIN_CHANNEL_SIZE_SATS ||
      newValue > cubby.amountThatCouldBeUsedToOpenChannels
    ) {
      setSatoshisError(
        `You can open a channel of between ${MIN_CHANNEL_SIZE_SATS.toLocaleString()} and ${cubby.amountThatCouldBeUsedToOpenChannels.toLocaleString()} satoshis`
      );
    } else {
      setSatoshisError("");
    }
  };

  const handleSubmit = () => {
    if (!selectedPeer) return;

    setIsLoading(true); // Set loading to true

    const publicKey = selectedPeer.value;
    const currentBalancePlusPossibleFees =
      cubby.safeChainBalance + EACH_CLOSURE_MIGHT_COST;

    if (cubby.satoshisAmountForOpenChannel > currentBalancePlusPossibleFees) {
      cubby.addNewErrorAndUpdateCubbyErrors(
        `Insufficient onchain balance to open channel of ${cubby.satoshisAmountForOpenChannel} satoshis. Please deposit more funds.`
      );
      cubby.setModal(null); // Close the modal
      setIsLoading(false); // Reset loading
      return;
    }

    cubby.cubbyLife("open_channel", {
      publicKey,
      satoshis: cubby.satoshisAmountForOpenChannel,
      isPrivate,
    });

    setTimeout(() => {
      setIsLoading(false);
      cubby.setModal(null); // Close the modal after 2 seconds // Reset loading state
    }, 4000);
  };

  const satoshisToBTC = (satoshis: number) => (satoshis / 100000000).toFixed(8);

  const countChannels = () => {
    const openChannels = cubby.channelsList.data.filter(
      (channel) =>
        channel.partner_public_key === selectedPeer?.value &&
        channel.is_active &&
        !channel.is_closing
    ).length;

    const pendingChannels = cubby.channelsList.data.filter(
      (channel) =>
        channel.partner_public_key === selectedPeer?.value &&
        (channel.is_opening || channel.is_closing)
    ).length;

    return { openChannels, pendingChannels };
  };

  const { openChannels, pendingChannels } = countChannels();

  const existingChannelsWarning =
    selectedPeer?.value === recommendedPublicKeyForChannelOpen
      ? "" // No warning if it's the recommended public key
      : openChannels
      ? `You already have ${openChannels} open channel(s)${
          pendingChannels > 0
            ? ` and ${pendingChannels} pending channel(s)`
            : ""
        } with this node.`
      : "";

  return (
    <>
      <Modal
        opened={cubby.currentModal === MODALS.OPEN_CHANNEL_TO_OTHER_NODE}
        onClose={() => cubby.setModal(null)}
        title={
          <TextWithInfoAndIcon
            text="Open Channel To Peer"
            articleSlug={ARTICLES.OPENING_CHANNELS.slug}
            icon={<IconExchange size={24} />}
            cubby={cubby}
            textSize="lg"
          />
        }
        centered
      >
        {cubby.hasEnoughFundsOnchainToOpenChannel ? (
          <Stack>
            <form>
              <Select
                description="Select a peer"
                error={error}
                placeholder="Select a peer"
                value={selectedPeer?.value}
                onChange={handlePeerChange}
                data={peersData}
                disabled={satoshisError.length > 0}
              />
              <Text mt="sm" mb="sm" size="xs"></Text>
              <NumberInput
                label="Satoshis"
                // description={`BTC: ${satoshisToBTC(
                //   cubby.satoshisAmountForOpenChannel
                // )}`}
                error={satoshisError}
                value={cubby.satoshisAmountForOpenChannel}
                onChange={handleSatoshisChange}
                min={50000}
                max={500000000}
                step={10000}
                required
              />
              <SatoshisInfo
                cubby={cubby}
                satoshis={cubby.satoshisAmountForOpenChannel}
              />
              <GenericEstimatedFee cubby={cubby} />
              <Checkbox
                label={"Private Channel"}
                checked={isPrivate}
                onChange={(event) => setIsPrivate(event.currentTarget.checked)}
              />
            </form>

            {existingChannelsWarning && (
              <Text c="red" mt="md">
                {existingChannelsWarning}
              </Text>
            )}
            <Group mt="lg">
              <Button
                onClick={handleSubmit}
                loading={isLoading} // Add loading state to button
                disabled={isLoading} // Disable button while loading
              >
                Open Channel
              </Button>
            </Group>
          </Stack>
        ) : (
          <Paper>
            <Stack>
              <Text>To open a channel, please deposit more BTC.</Text>
              <Text>
                Your current onchain balance is{" "}
                {cubby.safeChainBalance.toLocaleString()} satoshis.
              </Text>
              <Text>
                Given current onchain fees, you need at least{" "}
                {cubby.minimumOnchainFundsToOpenChannel.toLocaleString()}{" "}
                satoshis to open a channel.
              </Text>
            </Stack>
          </Paper>
        )}
      </Modal>
    </>
  );
});

export default OpenChannelToOtherNode;
